import React from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, ExternalLink, KeyRound, Globe, Mail, Webhook, Zap, LayoutGrid } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Github, Gitlab as GitlabIcon } from 'lucide-react'; // Using Gitlab from lucide-react if available, else another icon
// Note: lucide-react might not have azure, we will use a generic icon or custom SVG for Azure.
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useSearchParams } from 'react-router-dom';

interface IJiraConfig {
    domain: string;
    email: string;
    enabled: boolean;
}

interface IFormState {
    domain: string;
    email: string;
    apiToken: string;
}

type FeedbackState = { type: 'success' | 'error'; message: string } | null;

const getApiUrl = () =>
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
        ? import.meta.env.VITE_API_URL
        : 'http://localhost:3000';

// Shared input class — consistent across all fields in this tab
const INPUT_CLASS =
    'w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gh-border-dark rounded-lg ' +
    'bg-white dark:bg-gh-bg-dark text-slate-900 dark:text-slate-200 ' +
    'placeholder-slate-400 dark:placeholder-slate-500 ' +
    'focus:outline-none focus:ring-2 focus:ring-gh-accent dark:focus:ring-gh-accent-dark focus:border-gh-accent dark:focus:border-gh-accent-dark transition';

export function IntegrationsTab() {
    const { user, token } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // Fix #5 — detect tour=connectors param and trigger a 1-step driver.js popover
    // pointing at the Slack webhook card. Strip the param afterward so it doesn't
    // re-fire on refresh.
    React.useEffect(() => {
        if (searchParams.get('tour') !== 'connectors') return;

        // Remove the param from the URL immediately (replace so Back doesn't re-trigger)
        const next = new URLSearchParams(searchParams);
        next.delete('tour');
        setSearchParams(next, { replace: true });

        // Give the tab a moment to fully paint before the popover fires
        const timerId = setTimeout(() => {
            let connectorsTour: ReturnType<typeof driver>;
            connectorsTour = driver({
                animate: true,
                overlayColor: 'rgba(15, 23, 42, 0.7)',
                stagePadding: 8,
                stageRadius: 8,
                popoverClass: 'agnox-tour-popover',
                doneBtnText: 'Got it',
                onDestroyStarted: () => {
                    connectorsTour.destroy();
                },
                steps: [
                    {
                        element: '[data-testid="integrations-container"]',
                        popover: {
                            title: 'Add Your Webhooks & Integrations',
                            description:
                                'Integrate with Slack, Jira, GitHub, and more. Add your Slack webhook URL to get real-time alerts whenever a test run fails, or connect Jira to auto-create tickets from failures.',
                            side: 'top',
                            align: 'start',
                        },
                    },
                ],
            });
            connectorsTour.drive();
        }, 400);

        return () => clearTimeout(timerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once on mount — navigate already stripped the param

    // ── Jira state ────────────────────────────────────────────────────────────
    const [form, setForm] = React.useState<IFormState>({ domain: '', email: '', apiToken: '' });
    const [isConfigured, setIsConfigured] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [testing, setTesting] = React.useState(false);
    const [saveFeedback, setSaveFeedback] = React.useState<FeedbackState>(null);
    const [testFeedback, setTestFeedback] = React.useState<FeedbackState>(null);

    // ── Linear state ──────────────────────────────────────────────────────────
    const [linearForm, setLinearForm] = React.useState({ apiKey: '', teamId: '' });
    const [isLinearConfigured, setIsLinearConfigured] = React.useState(false);
    const [savingLinear, setSavingLinear] = React.useState(false);
    const [linearFeedback, setLinearFeedback] = React.useState<FeedbackState>(null);

    // ── Monday.com state ──────────────────────────────────────────────────────
    const [mondayForm, setMondayForm] = React.useState({ apiToken: '', boardId: '', groupId: '' });
    const [isMondayConfigured, setIsMondayConfigured] = React.useState(false);
    const [savingMonday, setSavingMonday] = React.useState(false);
    const [mondayFeedback, setMondayFeedback] = React.useState<FeedbackState>(null);

    // ── Generic Webhook state ─────────────────────────────────────────────────
    const [webhookForm, setWebhookForm] = React.useState({ url: '', secret: '' });
    const [isWebhookConfigured, setIsWebhookConfigured] = React.useState(false);
    const [savingWebhook, setSavingWebhook] = React.useState(false);
    const [webhookFeedback, setWebhookFeedback] = React.useState<FeedbackState>(null);

    // ── Unlink state ──────────────────────────────────────────────────────────
    const [unlinkTarget, setUnlinkTarget] = React.useState<string | null>(null);
    const [unlinking, setUnlinking] = React.useState(false);
    const [unlinkFeedback, setUnlinkFeedback] = React.useState<FeedbackState>(null);

    // ── Slack state ───────────────────────────────────────────────────────────
    const isAdmin = user?.role === 'admin';
    const [slackUrl, setSlackUrl] = React.useState('');
    const [slackOriginalUrl, setSlackOriginalUrl] = React.useState('');
    const [slackNotificationEvents, setSlackNotificationEvents] = React.useState<string[]>(['FAILED', 'ERROR', 'UNSTABLE']);
    const [slackLoading, setSlackLoading] = React.useState(true);
    const [slackSaving, setSlackSaving] = React.useState(false);
    const [slackFeedback, setSlackFeedback] = React.useState<FeedbackState>(null);

    // ── MS Teams state ────────────────────────────────────────────────────────
    const [teamsUrl, setTeamsUrl] = React.useState('');
    const [teamsOriginalUrl, setTeamsOriginalUrl] = React.useState('');
    const [teamsNotificationEvents, setTeamsNotificationEvents] = React.useState<string[]>(['FAILED', 'ERROR', 'UNSTABLE']);
    const [teamsLoading, setTeamsLoading] = React.useState(true);
    const [teamsSaving, setTeamsSaving] = React.useState(false);
    const [teamsFeedback, setTeamsFeedback] = React.useState<FeedbackState>(null);

    // ── CI Integrations state ─────────────────────────────────────────────────
    const [ciState, setCiState] = React.useState({
        github: { token: '', enabled: false, saving: false, feedback: null as FeedbackState },
        gitlab: { token: '', enabled: false, saving: false, feedback: null as FeedbackState },
        azure: { token: '', enabled: false, saving: false, feedback: null as FeedbackState },
        bitbucket: { token: '', enabled: false, saving: false, feedback: null as FeedbackState },
    });

    const authHeaders = React.useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    React.useEffect(() => {
        let cancelled = false;
        const API_URL = getApiUrl();

        const fetchConfig = async () => {
            try {
                const res = await axios.get<{ success: boolean; data?: IJiraConfig }>(
                    `${API_URL}/api/integrations/jira`,
                    { headers: authHeaders },
                );
                if (!cancelled && res.data.success && res.data.data) {
                    const cfg = res.data.data;
                    setForm({ domain: cfg.domain ?? '', email: cfg.email ?? '', apiToken: '' });
                    setIsConfigured(cfg.enabled ?? false);
                }
            } catch {
                // Not yet configured — silently ignore 404
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchConfig();
        return () => { cancelled = true; };
    }, [authHeaders]);

    // ── Slack fetch ───────────────────────────────────────────────────────────
    React.useEffect(() => {
        let cancelled = false;
        const API_URL = getApiUrl();

        const fetchOrg = async (): Promise<void> => {
            try {
                const res = await axios.get<{ success: boolean; organization: any }>(
                    `${API_URL}/api/organization`,
                    { headers: authHeaders },
                );
                if (!cancelled && res.data.success) {
                    const org = res.data.organization;
                    const url = org.slackWebhookUrl ?? '';
                    setSlackUrl('');
                    setSlackOriginalUrl(url);
                    setSlackNotificationEvents(org.slackNotificationEvents ?? ['FAILED', 'ERROR', 'UNSTABLE']);

                    setTeamsUrl('');
                    setTeamsOriginalUrl(org.msTeamsWebhookUrl ?? '');
                    setTeamsNotificationEvents(org.msTeamsNotificationEvents ?? ['FAILED', 'ERROR', 'UNSTABLE']);

                    const integrations = org.integrations || {};
                    setIsConfigured(integrations.jira?.enabled || false);
                    setIsLinearConfigured(integrations.linear?.enabled || false);
                    if (integrations.linear?.teamId) {
                        setLinearForm(prev => ({ ...prev, teamId: integrations.linear.teamId }));
                    }
                    setIsMondayConfigured(integrations.monday?.enabled || false);
                    if (integrations.monday?.boardId) {
                        setMondayForm(prev => ({ ...prev, boardId: integrations.monday.boardId, groupId: integrations.monday.groupId ?? '' }));
                    }
                    setIsWebhookConfigured(integrations.webhook?.enabled || false);
                    if (integrations.webhook?.url) {
                        setWebhookForm(prev => ({ ...prev, url: integrations.webhook.url }));
                    }
                    setCiState(prev => ({
                        ...prev,
                        github: { ...prev.github, enabled: integrations.github?.enabled || false },
                        gitlab: { ...prev.gitlab, enabled: integrations.gitlab?.enabled || false },
                        azure: { ...prev.azure, enabled: integrations.azure?.enabled || false },
                        bitbucket: { ...prev.bitbucket, enabled: integrations.bitbucket?.enabled || false },
                    }));
                }
            } catch {
                // Non-critical — silently ignore
            } finally {
                if (!cancelled) {
                    setSlackLoading(false);
                    setTeamsLoading(false);
                }
            }
        };

        fetchOrg();
        return () => { cancelled = true; };
    }, [authHeaders]);

    const handleSlackSave = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setSlackSaving(true);
        setSlackFeedback(null);
        const API_URL = getApiUrl();

        try {
            const payload: any = {
                slackNotificationEvents
            };
            if (slackUrl.trim()) {
                payload.slackWebhookUrl = slackUrl.trim();
            }
            const res = await axios.patch<{ success: boolean }>(
                `${API_URL}/api/organization`,
                payload,
                { headers: authHeaders },
            );
            if (res.data.success) {
                if (slackUrl.trim()) {
                    setSlackOriginalUrl(slackUrl.trim());
                }
                setSlackUrl('');
                setSlackFeedback({ type: 'success', message: 'Slack webhook saved successfully.' });
                setTimeout(() => setSlackFeedback(null), 3000);
            }
        } catch (err: any) {
            setSlackFeedback({
                type: 'error',
                message: err?.response?.data?.message ?? 'Failed to save Slack webhook.',
            });
        } finally {
            setSlackSaving(false);
        }
    };

    const handleTeamsSave = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setTeamsSaving(true);
        setTeamsFeedback(null);
        const API_URL = getApiUrl();

        try {
            const payload: any = {
                msTeamsNotificationEvents: teamsNotificationEvents
            };
            if (teamsUrl.trim()) {
                payload.msTeamsWebhookUrl = teamsUrl.trim();
            }
            const res = await axios.patch<{ success: boolean }>(
                `${API_URL}/api/organization`,
                payload,
                { headers: authHeaders },
            );
            if (res.data.success) {
                if (teamsUrl.trim()) {
                    setTeamsOriginalUrl(teamsUrl.trim());
                }
                setTeamsUrl('');
                setTeamsFeedback({ type: 'success', message: 'MS Teams webhook saved successfully.' });
                setTimeout(() => setTeamsFeedback(null), 3000);
            }
        } catch (err: any) {
            setTeamsFeedback({
                type: 'error',
                message: err?.response?.data?.message ?? 'Failed to save MS Teams webhook.',
            });
        } finally {
            setTeamsSaving(false);
        }
    };

    const handleCiSave = async (e: React.FormEvent, provider: 'github' | 'gitlab' | 'azure' | 'bitbucket') => {
        e.preventDefault();
        const tokenVal = ciState[provider].token.trim();
        if (!tokenVal) return;

        setCiState(prev => ({
            ...prev,
            [provider]: { ...prev[provider], saving: true, feedback: null }
        }));

        const API_URL = getApiUrl();
        try {
            const res = await axios.patch(
                `${API_URL}/api/organization/integrations/${provider}`,
                { token: tokenVal, enabled: true },
                { headers: authHeaders }
            );
            if (res.data.success) {
                setCiState(prev => ({
                    ...prev,
                    [provider]: { token: '', enabled: true, saving: false, feedback: { type: 'success', message: `${provider} token saved successfully.` } }
                }));
                setTimeout(() => {
                    setCiState(prev => ({
                        ...prev,
                        [provider]: { ...prev[provider], feedback: null }
                    }));
                }, 3000);
            }
        } catch (err: any) {
            setCiState(prev => ({
                ...prev,
                [provider]: { ...prev[provider], saving: false, feedback: { type: 'error', message: err?.response?.data?.error ?? `Failed to save ${provider} token.` } }
            }));
        }
    };

    const handleCiTokenChange = (provider: 'github' | 'gitlab' | 'azure' | 'bitbucket', value: string) => {
        setCiState(prev => ({
            ...prev,
            [provider]: { ...prev[provider], token: value }
        }));
    };

    const handleLinearSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!linearForm.apiKey.trim() || !linearForm.teamId.trim()) return;
        setSavingLinear(true);
        setLinearFeedback(null);
        const API_URL = getApiUrl();

        try {
            await axios.put(
                `${API_URL}/api/integrations/linear`,
                { token: linearForm.apiKey.trim(), teamId: linearForm.teamId.trim(), enabled: true },
                { headers: authHeaders },
            );
            setIsLinearConfigured(true);
            setLinearForm(prev => ({ ...prev, apiKey: '' }));
            setLinearFeedback({ type: 'success', message: 'Linear integration saved successfully.' });
            setTimeout(() => setLinearFeedback(null), 3000);
        } catch (err: any) {
            setLinearFeedback({ type: 'error', message: err?.response?.data?.error ?? 'Failed to save Linear configuration.' });
        } finally {
            setSavingLinear(false);
        }
    };

    const handleMondaySave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mondayForm.boardId.trim()) return;
        setSavingMonday(true);
        setMondayFeedback(null);
        const API_URL = getApiUrl();

        try {
            await axios.put(
                `${API_URL}/api/integrations/monday`,
                {
                    token: mondayForm.apiToken.trim() || undefined,
                    boardId: mondayForm.boardId.trim(),
                    groupId: mondayForm.groupId.trim() || undefined,
                    enabled: true,
                },
                { headers: authHeaders },
            );
            setIsMondayConfigured(true);
            setMondayForm(prev => ({ ...prev, apiToken: '' }));
            setMondayFeedback({ type: 'success', message: 'Monday.com integration saved successfully.' });
            setTimeout(() => setMondayFeedback(null), 3000);
        } catch (err: any) {
            setMondayFeedback({ type: 'error', message: err?.response?.data?.error ?? 'Failed to save Monday.com configuration.' });
        } finally {
            setSavingMonday(false);
        }
    };

    const handleWebhookSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!webhookForm.url.trim()) return;
        setSavingWebhook(true);
        setWebhookFeedback(null);
        const API_URL = getApiUrl();

        try {
            await axios.put(
                `${API_URL}/api/integrations/webhook`,
                {
                    url: webhookForm.url.trim(),
                    ...(webhookForm.secret.trim() ? { secret: webhookForm.secret.trim() } : {}),
                    enabled: true,
                },
                { headers: authHeaders },
            );
            setIsWebhookConfigured(true);
            setWebhookForm(prev => ({ ...prev, secret: '' }));
            setWebhookFeedback({ type: 'success', message: 'Webhook integration saved successfully.' });
            setTimeout(() => setWebhookFeedback(null), 3000);
        } catch (err: any) {
            setWebhookFeedback({ type: 'error', message: err?.response?.data?.error ?? 'Failed to save webhook configuration.' });
        } finally {
            setSavingWebhook(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setSaveFeedback(null);
        const API_URL = getApiUrl();

        try {
            await axios.put(
                `${API_URL}/api/integrations/jira`,
                { domain: form.domain.trim(), email: form.email.trim(), token: form.apiToken },
                { headers: authHeaders },
            );
            setIsConfigured(true);
            setForm(prev => ({ ...prev, apiToken: '' }));
            setSaveFeedback({ type: 'success', message: 'Jira integration saved successfully.' });
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? 'Failed to save configuration.';
            setSaveFeedback({ type: 'error', message: msg });
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestFeedback(null);
        const API_URL = getApiUrl();

        try {
            const res = await axios.post<{ success: boolean; data?: { message?: string } }>(
                `${API_URL}/api/integrations/jira/test`,
                {},
                { headers: authHeaders },
            );
            const msg = res.data.data?.message ?? 'Connection successful!';
            setTestFeedback({ type: 'success', message: msg });
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? 'Connection test failed. Check your credentials.';
            setTestFeedback({ type: 'error', message: msg });
        } finally {
            setTesting(false);
        }
    };

    const handleUnlinkConfirm = async () => {
        if (!unlinkTarget) return;
        setUnlinking(true);
        setUnlinkFeedback(null);
        const API_URL = getApiUrl();

        try {
            await axios.delete(`${API_URL}/api/integrations/${unlinkTarget}`, { headers: authHeaders });

            // Reset local state for the unlinked provider
            if (unlinkTarget === 'jira') {
                setIsConfigured(false);
                setForm({ domain: '', email: '', apiToken: '' });
            } else if (unlinkTarget === 'linear') {
                setIsLinearConfigured(false);
                setLinearForm({ apiKey: '', teamId: '' });
            } else if (unlinkTarget === 'monday') {
                setIsMondayConfigured(false);
                setMondayForm({ apiToken: '', boardId: '', groupId: '' });
            } else if (unlinkTarget === 'webhook') {
                setIsWebhookConfigured(false);
                setWebhookForm({ url: '', secret: '' });
            } else if (['github', 'gitlab', 'azure', 'bitbucket'].includes(unlinkTarget)) {
                setCiState(prev => ({
                    ...prev,
                    [unlinkTarget]: { token: '', enabled: false, saving: false, feedback: null },
                }));
            }

            setUnlinkTarget(null);
            setUnlinkFeedback({ type: 'success', message: `${unlinkTarget.charAt(0).toUpperCase() + unlinkTarget.slice(1)} integration removed.` });
            setTimeout(() => setUnlinkFeedback(null), 4000);
        } catch (err: any) {
            setUnlinkFeedback({ type: 'error', message: err?.response?.data?.error ?? `Failed to unlink ${unlinkTarget}.` });
            setUnlinkTarget(null);
        } finally {
            setUnlinking(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin text-blue-500 dark:text-blue-400" />
                <span className="ml-3 text-sm text-slate-500 dark:text-slate-400">Loading integration settings...</span>
            </div>
        );
    }

    return (
        <div className="max-w-2xl" data-testid="integrations-container">
            {/* Unlink confirmation dialog */}
            {unlinkTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gh-bg-dark rounded-xl shadow-2xl border border-slate-200 dark:border-gh-border-dark p-6 w-full max-w-sm mx-4">
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            Remove {unlinkTarget.charAt(0).toUpperCase() + unlinkTarget.slice(1)} integration?
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
                            Stored credentials will be permanently deleted. You can reconnect at any time.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setUnlinkTarget(null)}
                                disabled={unlinking}
                                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUnlinkConfirm}
                                disabled={unlinking}
                                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors cursor-pointer disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {unlinking && <Loader2 size={14} className="animate-spin" />}
                                Remove Integration
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Unlink feedback banner */}
            {unlinkFeedback && (
                <div className={`mb-4 flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${unlinkFeedback.type === 'success'
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                    : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                }`}>
                    {unlinkFeedback.type === 'success' ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                    {unlinkFeedback.message}
                </div>
            )}

            {/* Section heading */}
            <div className="mb-6">
                <h2 className="text-base font-semibold text-slate-900 dark:text-gh-text-dark">Integrations</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Connect external services to extend your automation workflow.
                </p>
            </div>

            {/* Jira Card */}
            <div className="rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                    <div className="flex items-center gap-3">
                        {/* Jira-style icon badge */}
                        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                                <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35H17.5v1.61C17.5 10.36 19.46 12.3 21.85 12.3V2.85a.85.85 0 0 0-.85-.85H11.53ZM6.77 6.8a4.362 4.362 0 0 0 4.35 4.34h1.62v1.63a4.362 4.362 0 0 0 4.34 4.34V7.65a.85.85 0 0 0-.85-.85H6.77ZM2 11.6c0 2.4 1.97 4.35 4.35 4.35h1.62v1.62C7.97 20.03 9.94 22 12.32 22v-9.54a.85.85 0 0 0-.85-.85L2 11.6Z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">Jira</h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Atlassian Jira Software</p>
                        </div>
                    </div>
                    {isConfigured && (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                <CheckCircle2 size={12} />
                                Connected
                            </span>
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={() => setUnlinkTarget('jira')}
                                    className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors cursor-pointer"
                                >
                                    Unlink
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleSave} className="px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Connect your Jira workspace to create tickets directly from failed executions.
                        Your API token is encrypted with AES-256-GCM before storage.
                    </p>

                    {/* Domain */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="jira-domain">
                            Jira Domain
                        </label>
                        <div className="relative">
                            <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="jira-domain"
                                type="text"
                                required
                                placeholder="your-org.atlassian.net"
                                value={form.domain}
                                onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Your Atlassian subdomain, e.g.{' '}
                            <code className="bg-slate-100 dark:bg-slate-800 dark:text-slate-300 px-1 rounded">acme.atlassian.net</code>
                        </p>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="jira-email">
                            Account Email
                        </label>
                        <div className="relative">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="jira-email"
                                type="email"
                                required
                                placeholder="you@example.com"
                                value={form.email}
                                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">The email address associated with your Jira account.</p>
                    </div>

                    {/* API Token */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="jira-token">
                            API Token
                        </label>
                        <div className="relative">
                            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="jira-token"
                                type="password"
                                placeholder={isConfigured ? '••••••••  (leave blank to keep existing)' : 'Paste your API token'}
                                value={form.apiToken}
                                onChange={e => setForm(prev => ({ ...prev, apiToken: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Generate at{' '}
                            <a
                                href="https://id.atlassian.com/manage-profile/security/api-tokens"
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 inline-flex items-center gap-0.5"
                            >
                                Atlassian account settings <ExternalLink size={10} />
                            </a>
                        </p>
                    </div>

                    {/* Save feedback */}
                    {saveFeedback && (
                        <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${saveFeedback.type === 'success'
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                            }`}>
                            {saveFeedback.type === 'success'
                                ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                            }
                            <span>{saveFeedback.message}</span>
                        </div>
                    )}

                    {/* Test Connection feedback */}
                    {testFeedback && (
                        <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${testFeedback.type === 'success'
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                            }`}>
                            {testFeedback.type === 'success'
                                ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                            }
                            <span>{testFeedback.message}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-1">
                        <button
                            type="submit"
                            data-testid="jira-save-button"
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                        >
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {saving ? 'Saving…' : 'Save Configuration'}
                        </button>

                        {isConfigured && (
                            <button
                                type="button"
                                disabled={testing}
                                onClick={handleTest}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-gh-bg-subtle-dark border border-slate-300 dark:border-gh-border-dark rounded-lg hover:bg-slate-50 dark:hover:bg-gh-bg-dark disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                            >
                                {testing && <Loader2 size={14} className="animate-spin" />}
                                {testing ? 'Testing…' : 'Test Connection'}
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* ── Linear Card ───────────────────────────────────────────────── */}
            <div className="mt-6 rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Zap size={18} className="text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">Linear</h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Issue Tracking</p>
                        </div>
                    </div>
                    {isLinearConfigured && (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                <CheckCircle2 size={12} />
                                Connected
                            </span>
                            {isAdmin && (
                                <button type="button" onClick={() => setUnlinkTarget('linear')} className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors cursor-pointer">
                                    Unlink
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleLinearSave} className="px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Connect your Linear workspace to create issues directly from failed executions.
                        Your API key is encrypted with AES-256-GCM before storage.
                    </p>

                    {/* API Key */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="linear-api-key">
                            Linear API Key
                        </label>
                        <div className="relative">
                            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="linear-api-key"
                                type="password"
                                placeholder={isLinearConfigured ? '••••••••  (leave blank to keep existing)' : 'lin_api_…'}
                                value={linearForm.apiKey}
                                onChange={e => setLinearForm(prev => ({ ...prev, apiKey: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                    </div>

                    {/* Team ID */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="linear-team-id">
                            Linear Team ID
                        </label>
                        <div className="relative">
                            <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="linear-team-id"
                                type="text"
                                required
                                placeholder="e.g. TEAM_ID from your Linear workspace"
                                value={linearForm.teamId}
                                onChange={e => setLinearForm(prev => ({ ...prev, teamId: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Found in Linear → Settings → Team → Team ID.
                        </p>
                    </div>

                    {/* Feedback */}
                    {linearFeedback && (
                        <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${linearFeedback.type === 'success'
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                            }`}>
                            {linearFeedback.type === 'success'
                                ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                            }
                            <span>{linearFeedback.message}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-1">
                        <button
                            type="submit"
                            disabled={savingLinear || (!linearForm.apiKey.trim() && !isLinearConfigured) || !linearForm.teamId.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                        >
                            {savingLinear && <Loader2 size={14} className="animate-spin" />}
                            {savingLinear ? 'Saving…' : 'Save Configuration'}
                        </button>
                    </div>
                </form>
            </div>

            {/* ── Monday.com Card ───────────────────────────────────────────── */}
            <div className="mt-6 rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-rose-600 flex items-center justify-center flex-shrink-0">
                            <LayoutGrid size={18} className="text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">Monday.com</h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Work OS / Project Tracking</p>
                        </div>
                    </div>
                    {isMondayConfigured && (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                <CheckCircle2 size={12} />
                                Connected
                            </span>
                            {isAdmin && (
                                <button type="button" onClick={() => setUnlinkTarget('monday')} className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors cursor-pointer">
                                    Unlink
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleMondaySave} className="px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Connect your Monday.com workspace to create items directly from failed executions.
                        Your API token is encrypted with AES-256-GCM before storage.
                    </p>

                    {/* API Token */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="monday-api-token">
                            API Token
                        </label>
                        <div className="relative">
                            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="monday-api-token"
                                type="password"
                                placeholder={isMondayConfigured ? '••••••••  (leave blank to keep existing)' : 'Paste your Monday.com API token'}
                                value={mondayForm.apiToken}
                                onChange={e => setMondayForm(prev => ({ ...prev, apiToken: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Found in Monday.com → Profile → Developer → API v2 token.
                        </p>
                    </div>

                    {/* Board ID */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="monday-board-id">
                            Board ID
                        </label>
                        <div className="relative">
                            <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="monday-board-id"
                                type="text"
                                required
                                placeholder="e.g. 1234567890"
                                value={mondayForm.boardId}
                                onChange={e => setMondayForm(prev => ({ ...prev, boardId: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Found in the board URL: monday.com/boards/<strong>BOARD_ID</strong>
                        </p>
                    </div>

                    {/* Group ID (Optional) */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="monday-group-id">
                            Group ID <span className="font-normal text-slate-400">(optional)</span>
                        </label>
                        <div className="relative">
                            <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="monday-group-id"
                                type="text"
                                placeholder="e.g. topics"
                                value={mondayForm.groupId}
                                onChange={e => setMondayForm(prev => ({ ...prev, groupId: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Leave blank to add items to the board's default group.
                        </p>
                    </div>

                    {/* Feedback */}
                    {mondayFeedback && (
                        <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${mondayFeedback.type === 'success'
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                            }`}>
                            {mondayFeedback.type === 'success'
                                ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                            }
                            <span>{mondayFeedback.message}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-1">
                        <button
                            type="submit"
                            disabled={savingMonday || (!mondayForm.apiToken.trim() && !isMondayConfigured) || !mondayForm.boardId.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                        >
                            {savingMonday && <Loader2 size={14} className="animate-spin" />}
                            {savingMonday ? 'Saving…' : 'Save Configuration'}
                        </button>
                    </div>
                </form>
            </div>

            {/* ── Generic Webhook Card ──────────────────────────────────────── */}
            <div className="mt-6 rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                            <Webhook size={18} className="text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">Generic Webhook</h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Execution Finished Notifications</p>
                        </div>
                    </div>
                    {isWebhookConfigured && (
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                <CheckCircle2 size={12} />
                                Connected
                            </span>
                            {isAdmin && (
                                <button type="button" onClick={() => setUnlinkTarget('webhook')} className="text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors cursor-pointer">
                                    Unlink
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleWebhookSave} className="px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Agnox will send an HTTP <code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">POST</code> to this URL with a JSON payload every time an execution finishes.
                        Optionally provide a secret to enable HMAC SHA-256 request signing (<code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">x-agnox-signature</code> header).
                    </p>

                    {/* Webhook URL */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="webhook-url">
                            Webhook URL
                        </label>
                        <div className="relative">
                            <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="webhook-url"
                                type="url"
                                required
                                placeholder="https://your-server.com/hooks/agnox"
                                value={webhookForm.url}
                                onChange={e => setWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Must use HTTPS. Agnox will timeout after 10 seconds.
                        </p>
                    </div>

                    {/* HMAC Secret (optional) */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="webhook-secret">
                            Webhook Secret <span className="font-normal text-slate-400">(optional)</span>
                        </label>
                        <div className="relative">
                            <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                                id="webhook-secret"
                                type="password"
                                placeholder={isWebhookConfigured ? '••••••••  (leave blank to keep existing)' : 'Used for HMAC SHA-256 signature'}
                                value={webhookForm.secret}
                                onChange={e => setWebhookForm(prev => ({ ...prev, secret: e.target.value }))}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                            Stored encrypted with AES-256-GCM. When set, each request includes an <code className="font-mono bg-slate-100 dark:bg-slate-800 px-1 rounded">x-agnox-signature: sha256=&lt;hmac&gt;</code> header.
                        </p>
                    </div>

                    {/* Feedback */}
                    {webhookFeedback && (
                        <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${webhookFeedback.type === 'success'
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                            : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                            }`}>
                            {webhookFeedback.type === 'success'
                                ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                            }
                            <span>{webhookFeedback.message}</span>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-1">
                        <button
                            type="submit"
                            disabled={savingWebhook || !webhookForm.url.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-slate-700 hover:bg-slate-800 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                        >
                            {savingWebhook && <Loader2 size={14} className="animate-spin" />}
                            {savingWebhook ? 'Saving…' : 'Save Configuration'}
                        </button>
                    </div>
                </form>
            </div>

            {/* ── Slack Card ────────────────────────────────────────────────── */}
            <div data-testid="integrations-slack-card" className="mt-6 rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#4A154B] flex items-center justify-center flex-shrink-0">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">Slack</h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Incoming Webhook Notifications</p>
                        </div>
                    </div>
                    {slackOriginalUrl && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            <CheckCircle2 size={12} />
                            Connected
                        </span>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleSlackSave} className="px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Receive a Slack message with a deep link to the Investigation Hub whenever a test
                        execution finishes. Leave empty to disable notifications.
                    </p>

                    {slackLoading ? (
                        <div className="flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin text-slate-400" />
                            <span className="text-sm text-slate-400">Loading...</span>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label
                                    htmlFor="slack-webhook-url"
                                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5"
                                >
                                    <Webhook size={12} />
                                    Webhook URL
                                </label>
                                <div className="relative">
                                    <Webhook size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                                    <input
                                        id="slack-webhook-url"
                                        type="url"
                                        value={slackUrl}
                                        onChange={(e) => setSlackUrl(e.target.value)}
                                        disabled={!isAdmin}
                                        placeholder={slackOriginalUrl ? "••••••••••••••••  (leave blank to keep existing)" : "https://hooks.slack.com/services/..."}
                                        className={isAdmin ? INPUT_CLASS : INPUT_CLASS + ' opacity-60 cursor-not-allowed'}
                                    />
                                </div>
                                {!isAdmin && (
                                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                        Only organization admins can modify notification settings.
                                    </p>
                                )}
                            </div>

                            <div className="pt-2">
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">
                                    Notify on Events
                                </label>
                                <div className="space-y-2">
                                    {['PASSED', 'FAILED', 'ERROR', 'UNSTABLE'].map(status => (
                                        <label key={status} className={`flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 ${!isAdmin && 'opacity-60 cursor-not-allowed'}`}>
                                            <input
                                                type="checkbox"
                                                checked={slackNotificationEvents.includes(status)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSlackNotificationEvents(prev => [...prev, status]);
                                                    } else {
                                                        setSlackNotificationEvents(prev => prev.filter(s => s !== status));
                                                    }
                                                }}
                                                disabled={!isAdmin}
                                                className="w-4 h-4 text-[#4A154B] border-slate-300 dark:border-gh-border-dark rounded focus:ring-[#4A154B] disabled:opacity-60 disabled:cursor-not-allowed"
                                            />
                                            {status}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {slackFeedback && (
                                <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${slackFeedback.type === 'success'
                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                                    : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                                    }`}>
                                    {slackFeedback.type === 'success'
                                        ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                        : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                                    }
                                    <span>{slackFeedback.message}</span>
                                </div>
                            )}

                            {isAdmin && (
                                <div className="pt-1">
                                    <button
                                        type="submit"
                                        data-testid="slack-save-button"
                                        disabled={slackSaving}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#4A154B] hover:bg-[#611f69] rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-[#4A154B] focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                                    >
                                        {slackSaving && <Loader2 size={14} className="animate-spin" />}
                                        {slackSaving ? 'Saving…' : 'Save Webhook'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </form>
            </div>

            {/* ── MS Teams Card ─────────────────────────────────────────────── */}
            <div data-testid="integrations-teams-card" className="mt-6 rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#6264A7' }}>
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                                <rect x="2" y="2" width="9" height="9" rx="1" />
                                <rect x="13" y="2" width="9" height="9" rx="1" />
                                <rect x="2" y="13" width="9" height="9" rx="1" />
                                <rect x="13" y="13" width="9" height="9" rx="1" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">Microsoft Teams</h3>
                            <p className="text-xs text-slate-400 dark:text-slate-500">Incoming Webhook Notifications</p>
                        </div>
                    </div>
                    {teamsOriginalUrl && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                            <CheckCircle2 size={12} />
                            Connected
                        </span>
                    )}
                </div>

                {/* Form */}
                <form onSubmit={handleTeamsSave} className="px-5 py-5 space-y-4">
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                        Receive a Teams message with a deep link to the Investigation Hub whenever a test
                        execution finishes. Leave empty to disable notifications.
                    </p>

                    {teamsLoading ? (
                        <div className="flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin text-slate-400" />
                            <span className="text-sm text-slate-400">Loading...</span>
                        </div>
                    ) : (
                        <>
                            <div>
                                <label
                                    htmlFor="teams-webhook-url"
                                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5"
                                >
                                    <Webhook size={12} />
                                    Webhook URL
                                </label>
                                <div className="relative">
                                    <Webhook size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                                    <input
                                        id="teams-webhook-url"
                                        type="url"
                                        value={teamsUrl}
                                        onChange={(e) => setTeamsUrl(e.target.value)}
                                        disabled={!isAdmin}
                                        placeholder={teamsOriginalUrl ? "••••••••••••••••  (leave blank to keep existing)" : "https://xxxxx.webhook.office.com/webhookb2/..."}
                                        className={isAdmin ? INPUT_CLASS : INPUT_CLASS + ' opacity-60 cursor-not-allowed'}
                                    />
                                </div>
                                {!isAdmin && (
                                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                        Only organization admins can modify notification settings.
                                    </p>
                                )}
                            </div>

                            <div className="pt-2">
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">
                                    Notify on Events
                                </label>
                                <div className="space-y-2">
                                    {['PASSED', 'FAILED', 'ERROR', 'UNSTABLE'].map(status => (
                                        <label key={status} className={`flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 ${!isAdmin && 'opacity-60 cursor-not-allowed'}`}>
                                            <input
                                                type="checkbox"
                                                checked={teamsNotificationEvents.includes(status)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setTeamsNotificationEvents(prev => [...prev, status]);
                                                    } else {
                                                        setTeamsNotificationEvents(prev => prev.filter(s => s !== status));
                                                    }
                                                }}
                                                disabled={!isAdmin}
                                                className="w-4 h-4 border-slate-300 dark:border-gh-border-dark rounded focus:ring-[#6264A7] disabled:opacity-60 disabled:cursor-not-allowed"
                                                style={{ accentColor: '#6264A7' }}
                                            />
                                            {status}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {teamsFeedback && (
                                <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${teamsFeedback.type === 'success'
                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                                    : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                                    }`}>
                                    {teamsFeedback.type === 'success'
                                        ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                        : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                                    }
                                    <span>{teamsFeedback.message}</span>
                                </div>
                            )}

                            {isAdmin && (
                                <div className="pt-1">
                                    <button
                                        type="submit"
                                        data-testid="teams-save-button"
                                        disabled={teamsSaving}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark bg-[#6264A7] hover:bg-[#4f52a3]"
                                    >
                                        {teamsSaving && <Loader2 size={14} className="animate-spin" />}
                                        {teamsSaving ? 'Saving…' : 'Save Webhook'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </form>
            </div>

            {/* ── CI Providers ────────────────────────────────────────────────── */}
            {(['github', 'gitlab', 'azure', 'bitbucket'] as const).map(provider => {
                const config = ciState[provider];
                const labels = {
                    github: { title: 'GitHub', desc: 'Pull Request integration', icon: <Github size={20} className="text-white" /> },
                    gitlab: { title: 'GitLab', desc: 'Merge Request integration', icon: <GitlabIcon size={20} className="text-white" /> },
                    azure: {
                        title: 'Azure DevOps',
                        desc: 'Pull Request integration',
                        icon: (
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                                <path d="M16.5 2 L22 5 L22 19 L16.5 22 L11 19 L11 14 L16.5 17 L19 15.5 L19 8.5 L16.5 7 L11 10 L11 5 L16.5 2 Z M7.5 7 L2 10 L2 14 L7.5 17 L10 15.5 L10 8.5 L7.5 7 Z" />
                            </svg>
                        )
                    },
                    bitbucket: {
                        title: 'Bitbucket',
                        desc: 'Pull Request integration',
                        icon: (
                            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
                                <path d="M.778 1.213a.768.768 0 00-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 00.77-.646l3.27-20.03a.768.768 0 00-.768-.891zM14.52 15.53H9.522L8.17 8.466h7.561z" />
                            </svg>
                        )
                    }
                };
                const bgColors = {
                    github: 'bg-black dark:bg-slate-800',
                    gitlab: 'bg-[#FC6D26]',
                    azure: 'bg-[#0078D7]',
                    bitbucket: 'bg-[#0052CC]'
                };
                return (
                    <div key={provider} className="mt-6 rounded-xl border border-slate-300 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-gh-border-dark">
                            <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-lg ${bgColors[provider]} flex items-center justify-center flex-shrink-0`}>
                                    {labels[provider].icon}
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">{labels[provider].title}</h3>
                                    <p className="text-xs text-slate-400 dark:text-slate-500">{labels[provider].desc}</p>
                                </div>
                            </div>
                            {config.enabled && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                    <CheckCircle2 size={12} />
                                    Connected
                                </span>
                            )}
                        </div>

                        <form onSubmit={(e) => handleCiSave(e, provider)} className="px-5 py-5 space-y-4">
                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                Connect your {labels[provider].title} organization to automatically post AI Analysis reports as comments on {provider === 'gitlab' ? 'Merge Requests' : 'Pull Requests'}.
                            </p>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
                                    Personal Access Token (PAT)
                                </label>
                                <div className="relative">
                                    <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                                    <input
                                        type="password"
                                        placeholder={config.enabled ? '••••••••••••••••  (leave blank to keep existing)' : 'Paste your API token'}
                                        value={config.token}
                                        onChange={e => handleCiTokenChange(provider, e.target.value)}
                                        className={INPUT_CLASS}
                                    />
                                </div>
                            </div>

                            {config.feedback && (
                                <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm ${config.feedback.type === 'success'
                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                                    : 'bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400'
                                    }`}>
                                    {config.feedback.type === 'success'
                                        ? <CheckCircle2 size={15} className="flex-shrink-0 mt-0.5" />
                                        : <XCircle size={15} className="flex-shrink-0 mt-0.5" />
                                    }
                                    <span>{config.feedback.message}</span>
                                </div>
                            )}

                            <div className="pt-1">
                                <button
                                    type="submit"
                                    data-testid={`${provider}-save-button`}
                                    disabled={config.saving || !config.token.trim()}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gh-bg-dark"
                                >
                                    {config.saving && <Loader2 size={14} className="animate-spin" />}
                                    {config.saving ? 'Saving…' : 'Save Token'}
                                </button>
                            </div>
                        </form>
                    </div>
                );
            })}
        </div>
    );
}
